import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useCity } from "../context/CityContext";
import { LogOut, User, MapPin, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { photoUrl } from "../lib/photos";
import LanguagePicker from "./LanguagePicker";
import ThemeToggle from "./ThemeToggle";
import { useLang } from "../context/LangContext";

const CITY_CHIP_CLASSES = {
  live: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
  soon: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",
  none: "border-black/15 bg-white hover:border-[color:var(--primary)]",
};

function getCityChipClass(city) {
  if (!city) return CITY_CHIP_CLASSES.none;
  return city.status === "live" ? CITY_CHIP_CLASSES.live : CITY_CHIP_CLASSES.soon;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { city, setPickerOpen } = useCity();
  const { t } = useLang();
  const nav = useNavigate();
  const isLive = city?.status === "live";
  const chipClass = getCityChipClass(city);
  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo">
          <Logo />
        </Link>
        <nav className="flex items-center gap-1 md:gap-3">
          <LanguagePicker />
          <ThemeToggle />
          <button
            data-testid="nav-city-chip"
            onClick={() => setPickerOpen(true)}
            className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${chipClass}`}
          >
            <MapPin className="w-3.5 h-3.5" />
            {city ? city.name : "Choose city"}
            {city && !isLive && <span className="text-[10px]">· Soon</span>}
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
          <Link to="/services" data-testid="nav-services" className="px-3 py-2 text-sm hover:text-[color:var(--secondary)] transition-colors">
            {t("nav_services")}
          </Link>
          {/* Unique Services — animated menu with live NEW badge */}
          <div className="relative group hidden md:inline-block" data-testid="nav-unique-services">
            <button className="px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 hover:text-[color:var(--secondary)] transition-colors">
              ✨ Unique Services
              <span className="relative inline-flex">
                <span className="absolute inset-0 rounded-full bg-red-500 opacity-70 animate-ping" />
                <span className="relative text-[9px] font-black uppercase rounded-full bg-red-500 text-white px-1.5 py-0.5 tracking-wider" data-testid="unique-new-badge">
                  NEW
                </span>
              </span>
              <ChevronDown className="w-3 h-3 opacity-60 transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute left-0 top-full pt-2 min-w-[240px] opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all z-50">
              <div className="rounded-2xl bg-white border border-black/10 shadow-xl overflow-hidden">
                <Link
                  to="/flatmate"
                  data-testid="nav-unique-flatmate"
                  className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50"
                >
                  <span className="w-8 h-8 rounded-full grid place-items-center text-white" style={{ background: "linear-gradient(135deg, #F97316, #10B981)" }}>🏠</span>
                  <span>
                    <span className="block font-bold">HeylpMATE Flatmate Assist</span>
                    <span className="block text-[11px] text-neutral-500">Verified rooms · Verified flatmates · Peace of mind</span>
                  </span>
                </Link>
                <Link
                  to="/namecraft"
                  data-testid="nav-unique-namecraft"
                  className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50 border-t border-black/5"
                >
                  <span className="w-8 h-8 rounded-full grid place-items-center text-white" style={{ background: "linear-gradient(135deg, #10B981, #00604A)" }}>🪶</span>
                  <span>
                    <span className="block font-bold">Unique NameCraft</span>
                    <span className="block text-[11px] text-neutral-500">Hand-crafted baby / brand / business names</span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
          <Link to="/earn" data-testid="nav-earn" className="px-3 py-2 text-sm font-bold hover:text-[color:var(--secondary)] transition-colors" style={{ color: "var(--secondary)" }}>
            💰 {t("nav_earn")}
          </Link>
          <Link to="/earn-by-helping" data-testid="nav-earn-by-helping" className="hidden md:inline px-3 py-2 text-sm hover:text-[color:var(--secondary)] transition-colors">
            📸 Earn by Helping
          </Link>
          <Link to="/partner-earning" data-testid="nav-partner-earning" className="hidden md:inline px-3 py-2 text-sm font-bold hover:text-[color:var(--secondary)] transition-colors"
                style={{ color: "#F45118" }}>
            💼 Partner · Earn from Home
          </Link>
          <Link to="/homestay" data-testid="nav-homestay" className="hidden md:inline px-3 py-2 text-sm font-bold hover:text-[color:var(--secondary)] transition-colors"
                style={{ color: "#00604A" }}>
            🏡 HomeStay Feel
          </Link>
          <Link to="/membership" data-testid="nav-membership" className="hidden md:inline px-3 py-2 text-sm hover:text-[color:var(--secondary)] transition-colors">
            {t("nav_membership")}
          </Link>
          <Link to="/about" data-testid="nav-about" className="hidden md:inline px-3 py-2 text-sm hover:text-[color:var(--secondary)] transition-colors">
            About
          </Link>
          <Link to="/become-mate" data-testid="nav-become-mate" className="hidden lg:inline px-3 py-2 text-sm hover:text-[color:var(--secondary)] transition-colors">
            Become a Mate
          </Link>
          {user ? (
            <>
              {user.role === "assistant" && (
                <Link to="/helper" data-testid="nav-helper" className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors">
                  🧾 Helper tasks
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="px-3 py-2 text-sm hover:text-[color:var(--secondary)] transition-colors flex items-center gap-2"
              >
                {user.photo_path ? (
                  <img src={photoUrl(user.photo_path)} alt={user.name} className="w-7 h-7 rounded-full object-cover border border-black/10" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span>{user.name.split(" ")[0]}</span>
              </Link>
              <Button
                data-testid="nav-logout-btn"
                variant="ghost"
                size="sm"
                onClick={() => {
                  logout();
                  toast.success("Signed out");
                  nav("/");
                }}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-sm">Sign in</Link>
              <Link to="/signup-quick" data-testid="nav-quick-signup">
                <button className="btn-primary text-sm">Quick Signup</button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
